import { useMemo } from 'react'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { useAppSelector } from 'hooks/reduxHooks'
import useOfferAvailableRates from 'hooks/useOfferAvailableRates'
import { groupBy, max } from 'lib/array/arrayUtils'
import { EmptyObject } from 'lib/object/objectUtils'
import { initialRooms } from 'lib/offer/occupancyUtils'
import { sha256 } from 'lib/utils/createHashUtils'
import { getAlwaysOnCancellationPolicyDays } from 'lib/payment/alwaysOnUtils'
import {
  isLPCUniqueOptionsEnabled,
  OfferPackageOptionView,
} from 'selectors/offerPage/offerPageSelectors'
import { ConnectionCancellationPolicy } from '@luxuryescapes/lib-refunds'

export default function useLPCUniqueOptions(
  offerId: string,
  options: Array<any>,
  params: {
    useDynamicCancellationPolicies?: boolean;
    duration?: number;
    checkIn?: string;
    rooms?: Array<App.Occupants>;
  } = EmptyObject,
): Array<OfferPackageOptionView> {
  const { checkIn, duration, rooms, useDynamicCancellationPolicies } = params
  const LPCUniqueOptionsEnabled = useAppSelector(isLPCUniqueOptionsEnabled)
  const checkOut = useMemo(() => checkIn ? moment(checkIn).add(duration, 'days').format(ISO_DATE_FORMAT) : undefined, [checkIn, duration])
  const offerAvailableRates = useOfferAvailableRates(offerId, {
    checkIn,
    checkOut,
    rooms: rooms ?? initialRooms,
  })

  const availableOptions = useMemo(() => {
    if (!LPCUniqueOptionsEnabled) {
      return options
    }
    const policies: Record<string, Array<ConnectionCancellationPolicy>> = {}
    if (useDynamicCancellationPolicies) {
      offerAvailableRates?.rates.map(rate => {
        policies[rate.packageUniqueKey] = rate.cancellationPolicies?.policies.map(policy => {
          return {
            deadline: policy.deadline,
            description: policy.description,
            nonRefundable: policy.nonRefundable,
            policyCode: policy.policyCode,
            timezone: policy.timezone,
          }
        })
      })
    }
    const groupedOptions = groupBy(
      options,
      view => view.package.id + '-' +
        (useDynamicCancellationPolicies ? sha256(JSON.stringify(policies[view.package.uniqueKey] ?? Date.now())) : view.package.roomRate?.cancellationPolicy?.type) + '-' +
        sha256(JSON.stringify(view.package.inclusions)) + '-' +
        sha256(JSON.stringify(view.package.bonusInclusions)) + '-' +
        sha256(JSON.stringify(view.package.luxPlusInclusionsByTier)),
    )
    if (!useDynamicCancellationPolicies) {
      const groupedOptionsByPrice = groupBy(
        Array.from(groupedOptions.values()).flatMap((views) => views[0]),
        view => view.package.id + '-' +
          (view.package.roomRate?.cancellationPolicy?.type === 'non-refundable' ? 'non-refundable' : 'refundable') + '-' +
          view.hotelPrice + '-' +
          view.flightPrice + '-' +
          sha256(JSON.stringify(view.package.inclusions)) + '-' +
          sha256(JSON.stringify(view.package.bonusInclusions)) + '-' +
          sha256(JSON.stringify(view.package.luxPlusInclusionsByTier)),
      )
      return Array.from(groupedOptionsByPrice.values())
        .flatMap((views) => max(views, view => getAlwaysOnCancellationPolicyDays(view.package.roomRate?.cancellationPolicy?.type)) ?? views[0])
    }
    return Array.from(groupedOptions.values()).flatMap((views) => views[0])
  }, [LPCUniqueOptionsEnabled, offerAvailableRates, options, useDynamicCancellationPolicies])

  return availableOptions
}
